import { FirestoreService } from '../../../sdk/firestoreService';
import { selectEffectiveLimit } from '../../../store/actions/licenseActions';

// Action Creators - משתמשים באותם action types כמו ב-dashboardActions
export const publishVersionStart = () => ({
    type: 'PUBLISH_VERSION_START'
});

export const publishVersionSuccess = (version) => ({
    type: 'PUBLISH_VERSION_SUCCESS',
    payload: { version }
});

export const publishVersionFailure = (error) => ({
    type: 'PUBLISH_VERSION_FAILURE',
    payload: error
});

export const updateDashboardInRedux = (dashboard) => ({
    type: 'FETCH_DASHBOARD_SUCCESS',
    payload: dashboard
});

/**
 * checkForUnpublishedChanges - בדיקה אם יש שינויים שלא פורסמו
 * @param {string} dashboardId - מזהה הדשבורד
 * @returns {Promise<boolean>} - האם יש שינויים
 */
export const checkForUnpublishedChanges = (dashboardId) => async () => {
    try {
        const dashboard = await FirestoreService.getDashboard(dashboardId);

        if (!dashboard) {
            return false;
        }

        // אם אין גרסאות בכלל, יש שינויים
        if (!dashboard.versions || dashboard.versions.length === 0) {
            return true;
        }

        // השווה את current לגרסה האחרונה
        const latestVersion = dashboard.versions[dashboard.versions.length - 1];
        const currentWidgets = dashboard.current?.widgets || [];
        const versionWidgets = latestVersion.widgets || [];

        // בדיקה פשוטה - אם מספר הווידג'טים שונה
        if (currentWidgets.length !== versionWidgets.length) {
            return true;
        }

        // נרמל את הווידג'טים - רק השדות החשובים
        const normalizeWidget = (widget) => ({
            widgetId: widget.widgetId,
            componentKey: widget.componentKey,
            templateId: widget.templateId,
            dashboardLayout: widget.dashboardLayout,
            props: widget.props,
            widgetSettings: widget.widgetSettings
        });

        // נרמל ונמיין לפי widgetId
        const normalizedCurrent = currentWidgets
            .map(normalizeWidget)
            .sort((a, b) => a.widgetId.localeCompare(b.widgetId));

        const normalizedVersion = versionWidgets
            .map(normalizeWidget)
            .sort((a, b) => a.widgetId.localeCompare(b.widgetId));

        const currentJson = JSON.stringify(normalizedCurrent);
        const versionJson = JSON.stringify(normalizedVersion);

        return currentJson !== versionJson;
    } catch (error) {
        console.error('Error checking for changes:', error);
        return false;
    }
};

/**
 * publishDashboardVersion - פרסום גירסה חדשה של הדשבורד
 * @param {string} dashboardId - מזהה הדשבורד
 * @param {Array} widgets - מערך הווידג'טים (אופציונלי - אם null, יטען מהדשבורד)
 * @param {Object} settings - הגדרות הדשבורד (אופציונלי - אם null, יטען מהדשבורד)
 */
export const publishDashboardVersion = (dashboardId, widgets = null, settings = null) => async (dispatch, getState) => {
    try {
        dispatch(publishVersionStart());

        const versionLimit = selectEffectiveLimit(getState(), 'versionHistory');

        // אם לא קיבלנו widgets/settings, נטען את הדשבורד
        let finalWidgets = widgets;
        let finalSettings = settings;

        if (!finalWidgets || !finalSettings) {
            const dashboard = await FirestoreService.getDashboard(dashboardId);
            if (!dashboard) {
                throw new Error('Dashboard not found');
            }
            finalWidgets = dashboard.current?.widgets || [];
            finalSettings = dashboard.current?.settings || { bgColor: '#fcfbfa', bgImage: '' };
        }

        const newVersion = await FirestoreService.publishDashboard(dashboardId, finalWidgets, finalSettings, versionLimit);

        // טען מחדש את הדשבורד כדי לעדכן את ה-Redux עם הגירסה החדשה
        const updatedDashboard = await FirestoreService.getDashboard(dashboardId);

        // עדכן את ה-Redux עם הדשבורד המעודכן
        dispatch(updateDashboardInRedux(updatedDashboard));

        dispatch(publishVersionSuccess(newVersion));
        return { success: true, version: newVersion };
    } catch (error) {
        console.error('❌ Error publishing version:', error);
        dispatch(publishVersionFailure(error.message));
        throw error;
    }
};
